import { type SignInResult, signInWithBackup } from "@/lib/auth-flows";
import {
	type IdentityBackup,
	validateIdentityBackup,
} from "@/lib/bap/identity-backup";
import { STORAGE_KEYS } from "@/lib/storage-keys";

type RestoreSignIn = (
	backup: IdentityBackup,
	options: {
		bapId: string;
		restoreExistingProfile: true;
		navigateOnRedirect: false;
	}
) => Promise<SignInResult>;

export class RestoreAuthorizationError extends Error {
	readonly code?: string;
	readonly retryable: boolean;

	constructor(message: string, code?: string, retryable = false) {
		super(message);
		this.name = "RestoreAuthorizationError";
		this.code = code;
		this.retryable = retryable;
	}
}

/** Authenticate first; durable browser storage is committed only on success. */
export async function authenticateAndPersistCloudBackup(input: {
	encryptedBackup: string;
	decryptedBackup: IdentityBackup;
	returnTo: string | null;
	selectedBapId: string;
	signIn?: RestoreSignIn;
}): Promise<string> {
	validateIdentityBackup(input.decryptedBackup);
	const result = await (input.signIn ?? signInWithBackup)(
		input.decryptedBackup,
		{
			bapId: input.selectedBapId,
			restoreExistingProfile: true,
			navigateOnRedirect: false,
		}
	);
	if (!(result.ok && !result.error)) {
		throw new RestoreAuthorizationError(
			result.error || "This backup could not be restored.",
			result.errorCode,
			result.retryable === true
		);
	}

	localStorage.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, input.encryptedBackup);
	return result.redirectURI || input.returnTo || "/";
}
